import resolveVanityHost from "~portal/helpers/vanityHost";

const SILVERFERN_APEX_HOST_PATTERN = /(?:^|\.)silverfern\.(?:red|com|local|app)$/;
const LOCAL_DEV_HOST_PATTERN = /^(localhost|127\.0\.0\.1)$/;
const DNS_LABEL_PATTERN = /^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/;
const ALPHABETIC_TLD_PATTERN = /^[a-z]{2,}$/;

export type HostClassification =
	| { kind: "app" }
	| { kind: "reserved"; label: string }
	| { kind: "vanityTenant"; tenantSlug: string }
	| { kind: "customDomainCandidate"; host: string };

export const normalizeHost = (host: string): string => host.toLowerCase().replace(/:\d+$/, "").replace(/\.$/, "");

const reviewAppHosts = (): string[] =>
	String(import.meta.env["VITE_APP_HOSTS"] ?? "")
		.split(",")
		.map((entry) => normalizeHost(entry.trim()))
		.filter((entry) => entry.length > 0);

export const isSilverfernAppHost = (host: string): boolean => {
	const normalized = normalizeHost(host);
	return (
		SILVERFERN_APEX_HOST_PATTERN.test(normalized) ||
		LOCAL_DEV_HOST_PATTERN.test(normalized) ||
		reviewAppHosts().includes(normalized)
	);
};

export const isClaimableHost = (host: string): boolean => {
	const labels = normalizeHost(host).split(".");
	if (labels.length < 2) return false;
	if (!labels.every((label) => DNS_LABEL_PATTERN.test(label))) return false;
	return ALPHABETIC_TLD_PATTERN.test(labels[labels.length - 1]);
};

const classifyHost = (host: string): HostClassification => {
	const vanity = resolveVanityHost(host);
	if (vanity.kind === "reserved") return { kind: "reserved", label: vanity.label };
	if (vanity.kind === "tenant") return { kind: "vanityTenant", tenantSlug: vanity.tenantSlug };
	if (isSilverfernAppHost(host)) return { kind: "app" };
	if (!isClaimableHost(host)) return { kind: "app" };
	return { kind: "customDomainCandidate", host: normalizeHost(host) };
};

export default classifyHost;
